import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { getLandlordDeals } from './deals-dashboard.api';
import type { GetLandlordDealsHTTPHandlerResponseData } from './application.types';
import { dealsTableKeys, type DealsListQuery } from './deals-table.query-keys';

/**
 * The deals list as a chain of pages. The API only returns the cursor for the next page, so each
 * page is requested with the cursor of the one before it; going back just reads a loaded page.
 * A new query (filters, sort, page size) starts a new chain, so no cursor outlives its filters.
 */
export const useFetchDeals = (query: DealsListQuery) =>
  useInfiniteQuery({
    queryKey: dealsTableKeys.list(query),
    queryFn: ({ pageParam }): Promise<GetLandlordDealsHTTPHandlerResponseData> =>
      getLandlordDeals({
        applicationTab: query.tab,
        limit: query.pageLimit,
        pageCursor: pageParam,
        search: query.search,
        selectedStatus: query.selectedStatus,
        selectedBuildings: query.selectedBuildings,
        leaseDates: query.leaseFilters,
        sortField: query.sortField,
        sortDirection: query.sortDirection,
        renewalOpportunityStatus: query.renewalOpportunityStatus,
        includeExpiredDeals: query.includeExpiredDeals,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.pagination?.page_cursor ?? undefined,
    staleTime: Infinity,
    // Keep the rows on screen while a filter, sort or page size changes, but never show one tab's
    // rows under another: the table stays mounted across tab switches.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2].tab === query.tab ? keepPreviousData(previous) : undefined,
    refetchOnWindowFocus: false,
  });
